import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import ComicCard from "@/components/feature/ComicCard";
import { comics } from "@/mocks/comics";
import { categories } from "@/mocks/categories";

export default function Categories() {
  const [params, setParams] = useSearchParams();
  const activeType = params.get("type") || "all";
  const keyword = params.get("q") || "";

  const filtered = useMemo(() => {
    let list = comics.slice();
    if (activeType !== "all") {
      const cat = categories.find((c) => c.slug === activeType);
      if (cat) list = list.filter((c) => c.category === cat.name);
    }
    if (keyword.trim()) {
      list = list.filter((c) => c.title.includes(keyword.trim()) || c.tags.some((t) => t.includes(keyword.trim())));
    }
    return list;
  }, [activeType, keyword]);

  const setFilter = (slug: string) => {
    const next = new URLSearchParams(params);
    if (slug === "all") next.delete("type");
    else next.set("type", slug);
    if (keyword.trim()) next.set("q", keyword.trim());
    setParams(next);
  };

  return (
    <div className="pt-16">
      {/* Breadcrumb */}
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-4 md:px-6">
          <nav className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-600" aria-label="面包屑导航">
            <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
            <i className="ri-arrow-right-s-line text-foreground-400"></i>
            <span className="font-medium text-foreground-950">全站分类</span>
          </nav>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-4xl">全站漫画总目录</h1>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-foreground-600 md:text-base">
            天堂漫画完整书库，全站漫画海量资源全收录。按类目或关键词筛选，快速定位你想看的每一部作品。
          </p>
        </div>

        {/* Filter chips */}
        <div className="mt-8 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => setFilter("all")}
            className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
              activeType === "all" ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700"
            }`}
          >
            全部漫画
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              onClick={() => setFilter(c.slug)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                activeType === c.slug ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        {keyword.trim() && (
          <p className="mt-6 text-center text-sm text-foreground-600">
            关键词「<span className="font-semibold text-primary-600">{keyword}</span>」共找到{" "}
            <span className="font-semibold text-foreground-950">{filtered.length}</span> 部作品
          </p>
        )}

        {filtered.length > 0 ? (
          <div className="mt-8 grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-3 lg:grid-cols-5 md:gap-6">
            {filtered.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        ) : (
          <div className="mt-16 text-center">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
              <i className="ri-search-line"></i>
            </span>
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">没有找到匹配的漫画</h2>
            <p className="mt-2 text-sm text-foreground-600">换个关键词，或在全库分类中重新筛选试试。</p>
          </div>
        )}
      </div>
    </div>
  );
}